/**
 * TcmLabPage — Platform-Admin-only diagnostic surface for the continuous
 * watch (Microsoft's tenant configuration monitoring) and its cutover from
 * the baseline scan.
 *
 * The route is gated by `TcmLabRoute` in App.tsx — by the time this page
 * mounts, the visitor is already confirmed `isPlatformAdmin === true`.
 *
 * Carbon layout (mockup 02 tables, contained tabs). The breadcrumb comes
 * from Layout; then:
 *   1. The brief (BriefHero): the page's one h1, action first, one lede and a
 *      provenance line naming the audience. It renders in every state, so a
 *      view that is wholly an empty state still has its h1.
 *   2. The tenant select (every lab action is per-tenant).
 *   3. The cutover playbook, a collapsible tile readable from any tab.
 *   4. Contained tabs, one per panel (Enablement, Monitors, Snapshots,
 *      Drift, Ingestion, Reconciliation), then the active panel. Each panel
 *      opens on a layer-01 surface the selected tab joins.
 *
 * Panels other than Enablement are lazy so a heavy panel can't block the
 * page from rendering.
 *
 * Voice: UI copy never names the vendor pipelines (blueprint §5.1 rule 7):
 * the Microsoft monitor pipeline is "the continuous watch" and the
 * PowerShell export "the baseline scan". Tab labels are bare nouns because
 * this is a diagnostic page.
 */

import { Suspense, lazy, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { BriefHero } from '@/components/carbon/BriefHero'
import { ContainedTabs } from '@/components/carbon/ContainedTabs'
import { Skeleton } from '@/components/carbon/Skeleton'
import { Tile } from '@/components/carbon/Tile'
import EmptyState from '@/components/shared/EmptyState'
import TcmCutoverPlaybook from '@/components/Tcm/TcmCutoverPlaybook'
import TcmEnablementPanel from '@/components/Tcm/TcmEnablementPanel'
import TcmTenantSelector from '@/components/Tcm/TcmTenantSelector'
import type { ActionBrief } from '@/lib/brief-shape'

// Lazy-loaded panels — each tab body is its own chunk, so a slow or missing
// panel can't break the page.
const TcmMonitorVersionTable = lazy(
  () => import('@/components/Tcm/TcmMonitorVersionTable'),
)
const TcmSnapshotPanel = lazy(() => import('@/components/Tcm/TcmSnapshotPanel'))
const TcmDriftTable = lazy(() => import('@/components/Tcm/TcmDriftTable'))
const TcmIngestionStatePanel = lazy(
  () => import('@/components/Tcm/TcmIngestionStatePanel'),
)
// Wave 8 Slice B — Reconciliation tab. Read-only diagnostic comparing
// active baseline-scan vs continuous-watch drifts for the selected tenant.
const TcmReconciliationTab = lazy(
  () => import('@/components/Tcm/TcmReconciliationTab'),
)

type TabKey =
  | 'enablement'
  | 'monitors'
  | 'snapshots'
  | 'drift'
  | 'ingestion'
  | 'reconciliation'

const ALL_TABS: ReadonlyArray<{ id: TabKey; label: string }> = [
  { id: 'enablement', label: 'Enablement' },
  { id: 'monitors', label: 'Monitors' },
  { id: 'snapshots', label: 'Snapshots' },
  { id: 'drift', label: 'Drift' },
  { id: 'ingestion', label: 'Ingestion' },
  { id: 'reconciliation', label: 'Reconciliation' },
]

// The page brief. There is no composer for a diagnostic page, so the copy
// lives here; BriefHero renders it (headline action first, one bold run in
// the lede).
const NO_TENANT_BRIEF: ActionBrief = {
  headline: 'Choose a tenant to open the lab.',
  lede: '**Every lab action** runs against one tenant, from enablement through the promote.',
}

// What each panel holds, for the no-tenant state under the tabs (the brief
// above already asks for the tenant, so this names what the choice unlocks).
const PANEL_PURPOSE: Record<TabKey, string> = {
  enablement: 'It walks the tenant from the enablement script to a verified watch.',
  monitors: 'It lists every registered monitor version against its instance budget.',
  snapshots: 'It captures a baseline of the resource types you choose.',
  drift: 'It lists the continuous watch’s drift, ready to accept or reject.',
  ingestion: 'It shows each monitor’s last ingestion cycle and its failures.',
  reconciliation: 'It compares the baseline scan with the continuous watch before a promote.',
}

const TENANT_BRIEF: ActionBrief = {
  headline: 'Run each cutover step from this lab.',
  lede: '**The cutover playbook** below takes a tenant from the baseline scan to Hybrid, one tab at a time.',
}

const PROVENANCE = 'Platform Admins only · Every action runs against the selected tenant'

export default function TcmLabPage() {
  const [searchParams, setSearchParams] = useSearchParams()

  // URL-synced state — Platform Admins can deep-link to a specific
  // tab+tenant combination. Tab default is `enablement` because every
  // other tab requires the tenant to be enabled first.
  const tabParam = searchParams.get('tab') as TabKey | null
  const tenantParam = searchParams.get('tenant')

  const [activeTab, setActiveTab] = useState<TabKey>(
    tabParam && ALL_TABS.some(t => t.id === tabParam) ? tabParam : 'enablement',
  )
  const [tenantId, setTenantId] = useState<string | null>(tenantParam ?? null)

  // Persist tab + tenant choice in the URL. We use `replace` so the back
  // button doesn't chain dozens of intra-lab navigations.
  useEffect(() => {
    const next = new URLSearchParams(searchParams)
    next.set('tab', activeTab)
    if (tenantId) {
      next.set('tenant', tenantId)
    } else {
      next.delete('tenant')
    }
    // Only update if something actually changed — avoids a render loop.
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true })
    }
  }, [activeTab, tenantId, searchParams, setSearchParams])

  // Most tabs require a tenant selection. The Enablement tab also requires
  // a tenant (every action is per-tenant). We surface the same empty
  // prompt on every tab when no tenant is picked, so users only have one
  // place to look for "what do I do first".
  const needsTenant = tenantId === null

  const activeBody = useMemo(() => {
    if (needsTenant) {
      const label = ALL_TABS.find(t => t.id === activeTab)?.label ?? 'This panel'
      return <NoTenantPrompt label={label} purpose={PANEL_PURPOSE[activeTab]} />
    }
    switch (activeTab) {
      case 'enablement':
        return <TcmEnablementPanel tenantId={tenantId!} />
      case 'monitors':
        return (
          <Suspense fallback={<TabFallback label="Loading monitors" />}>
            <TcmMonitorVersionTable tenantId={tenantId!} />
          </Suspense>
        )
      case 'snapshots':
        return (
          <Suspense fallback={<TabFallback label="Loading snapshots" />}>
            <TcmSnapshotPanel tenantId={tenantId!} />
          </Suspense>
        )
      case 'drift':
        return (
          <Suspense fallback={<TabFallback label="Loading drift" />}>
            <TcmDriftTable tenantId={tenantId!} />
          </Suspense>
        )
      case 'ingestion':
        return (
          <Suspense fallback={<TabFallback label="Loading ingestion state" />}>
            <TcmIngestionStatePanel tenantId={tenantId!} />
          </Suspense>
        )
      case 'reconciliation':
        return (
          <Suspense fallback={<TabFallback label="Loading reconciliation" />}>
            <TcmReconciliationTab tenantId={tenantId!} />
          </Suspense>
        )
      default:
        return null
    }
  }, [activeTab, tenantId, needsTenant])

  return (
    <div className="min-w-0">
      <section aria-labelledby="tcm-lab-verdict" className="mt-8">
        <BriefHero
          brief={needsTenant ? NO_TENANT_BRIEF : TENANT_BRIEF}
          size="pillar"
          headingId="tcm-lab-verdict"
          provenance={PROVENANCE}
        />
      </section>

      <div className="mt-8 max-w-[480px]">
        <TcmTenantSelector value={tenantId} onChange={setTenantId} />
      </div>

      {/* Cutover playbook — collapsible step-by-step for converting an
          existing tenant to Hybrid. Above the tabs so it can be read
          alongside the Enablement / Snapshots / Monitors controls while
          running a cutover. */}
      <div className="mt-12">
        <TcmCutoverPlaybook />
      </div>

      {/* One contained tab per panel; the selected tab joins the panel's
          layer-01 surface below it. */}
      <ContainedTabs
        aria-label="Lab sections"
        tabs={ALL_TABS.map(tab => ({ id: tab.id, label: tab.label }))}
        value={activeTab}
        onChange={id => setActiveTab(id as TabKey)}
        className="mt-12"
      />

      {/* Active tab body */}
      <div role="tabpanel" aria-label={`${activeTab} panel`} className="min-w-0">
        {activeBody}
      </div>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Local subcomponents                                                        */
/* -------------------------------------------------------------------------- */

function NoTenantPrompt({ label, purpose }: { label: string; purpose: string }) {
  // Voice: the brief above already asks for the tenant, so this names the
  // panel and what it holds rather than repeating the ask (CLAUDE.md product
  // voice, one line per slot). The mockup assumes one tenant is always
  // picked, so this copy is original.
  return (
    <Tile padding={24} className="min-w-0">
      <EmptyState headline={`${label} loads once a tenant is chosen.`} body={purpose} />
    </Tile>
  )
}

function TabFallback({ label }: { label: string }) {
  return (
    <Tile padding={24} className="min-w-0">
      <Skeleton lines={3} label={label} className="max-w-[640px]" />
    </Tile>
  )
}
